import { useState, useEffect } from "react";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Settings, Check, Loader2, Palette } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import { THEMES } from "@/lib/themes";
import ThemedIcon from "@/components/ThemedIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GlobalSettings() {
  const { settings, fetchSettings } = useSettings();
  const { user } = useAuth();
  const isSA = user?.role === "system_admin";
  const [form, setForm] = useState({
    help_email: "",
    dashboard_message: "",
    theme_id: "default",
  });
  const [saving, setSaving] = useState(false);
  const settingsRef = JSON.stringify(settings);

  useEffect(() => {
    if (settings) {
      setForm({
        help_email: settings.help_email || "",
        dashboard_message: settings.dashboard_message || "",
        theme_id: settings.theme_id || "default",
      });
    }
  }, [settingsRef]);

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      const payload = isSA ? form : { help_email: form.help_email, dashboard_message: form.dashboard_message };
      await axios.put(`${API}/settings`, payload, { withCredentials: true });
      await fetchSettings();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="global-settings-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: "var(--admin-text)" }}>
            Global Settings
          </h2>
          <p className="font-body text-sm mt-1" style={{ color: "var(--admin-muted)" }}>
            Configure your platform defaults.
          </p>
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="settings-save-button"
          className="h-10 px-5 font-body font-semibold rounded-lg transition-all duration-200 hover:opacity-90 shrink-0"
          style={{
            backgroundColor: "var(--theme-secondary)",
            color: "var(--on-secondary)",
          }}
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Saving</>
          ) : (
            <><Check className="h-4 w-4 mr-1.5" />Save Settings</>
          )}
        </Button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Theme picker — SA-only */}
        <div className="border rounded-xl p-6 space-y-5" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }} data-testid="theme-picker-card">
          <div className="flex items-center gap-3 pb-4 border-b" style={{ borderColor: "var(--admin-container-border)" }}>
            <ThemedIcon icon={Palette} size="md" />
            <h3 className="font-body text-lg font-bold" style={{ color: "var(--admin-container-text)" }}>Theme</h3>
            {!isSA && (
              <span className="ml-auto text-xs font-body px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                Read-only — System Admin only
              </span>
            )}
          </div>
          <p className="text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
            Sets the sidebar + top-nav colour for everyone in your org (admins, instructors, learners). Status badges (Published / Draft / Error) stay the same across every theme.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="theme-grid">
            {THEMES.map((t) => {
              const selected = form.theme_id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  disabled={!isSA}
                  onClick={() => setForm({ ...form, theme_id: t.id })}
                  data-testid={`theme-option-${t.id}`}
                  className={`relative group text-left rounded-xl overflow-hidden border-2 transition-all ${
                    selected ? "ring-2 ring-offset-2" : "hover:opacity-90"
                  } ${!isSA ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                  style={{
                    borderColor: selected ? t.secondary : "var(--admin-container-border)",
                  }}
                >
                  <div className="h-14 flex" style={{ backgroundColor: t.primary }}>
                    <div className="w-1/3 h-full" style={{ backgroundColor: t.secondary }} />
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-body font-semibold truncate" style={{ color: "var(--admin-container-text)" }}>{t.name}</p>
                    <p className="text-[10px] font-mono text-[#9CA3AF]">{t.primary}</p>
                  </div>
                  {selected && (
                    <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full flex items-center justify-center" style={{ backgroundColor: t.secondary }}>
                      <Check className="h-3 w-3" style={{ color: t.primary }} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Platform config */}
        <div className="border rounded-xl p-6 space-y-6" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <div className="flex items-center gap-3 pb-4 border-b" style={{ borderColor: "var(--admin-container-border)" }}>
            <ThemedIcon icon={Settings} size="md" />
            <h3 className="font-body text-lg font-bold" style={{ color: "var(--admin-container-text)" }}>Platform Configuration</h3>
          </div>

          <div className="space-y-2">
            <Label className="font-body text-sm" style={{ color: "var(--admin-container-muted)" }}>Help Email</Label>
            <Input type="email" value={form.help_email} onChange={(e) => setForm({ ...form, help_email: e.target.value })} placeholder="support@dinaxlp.com" data-testid="settings-help-email"
              className="h-11" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }} />
            <p className="text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>Displayed as the "Need Help" contact in the top navigation.</p>
          </div>

          <div className="space-y-2">
            <Label className="font-body text-sm" style={{ color: "var(--admin-container-muted)" }}>Dashboard Welcome Message</Label>
            <textarea value={form.dashboard_message} onChange={(e) => setForm({ ...form, dashboard_message: e.target.value })} placeholder="Welcome to your learning portal!" data-testid="settings-dashboard-message"
              rows={3}
              className="w-full rounded-md px-3 py-2 text-sm font-body h-auto resize-y"
              style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)", border: "1px solid" }} />
            <p className="text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>Shown as a welcome banner at the top of the learner dashboard. Leave empty to hide.</p>
          </div>
        </div>
      </form>
    </div>
  );
}
